import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Globe, MessageCircle, Menu, X, ShieldCheck, ArrowRight, Sun, Moon } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Logo } from "./Logo";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";

const NavItem = ({ to, children }) => (
  <NavLink
    to={to}
    end={to === "/"}
    className={({ isActive }) =>
      `text-sm font-medium transition-colors ${isActive ? "text-[#1E5FD9]" : "text-slate-700 hover:text-[#1E5FD9]"}`
    }
    data-testid={`nav-${to === "/" ? "home" : to.slice(1)}`}
  >
    {children}
  </NavLink>
);

export const Header = () => {
  const { lang, setLang, currency, setCurrency, settings, tr } = useApp();
  const { theme, toggle: toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/phones?q=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false); setOpen(false);
    }
  };

  const waLink = `https://wa.me/${settings.whatsapp_sales}?text=${encodeURIComponent(tr.whatsappMsgs.general)}`;

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20 gap-4">
          <Link to="/" aria-label="AFC Telecom Home" data-testid="header-logo-link">
            <Logo className="h-10 lg:h-12" />
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            <NavItem to="/">{tr.nav.home}</NavItem>
            <NavItem to="/phones">{tr.nav.phones}</NavItem>
            <NavItem to="/phones?brand=Blackview"><span className="text-[#1E5FD9] font-semibold">Blackview</span></NavItem>
            <NavItem to="/about">{tr.nav.about}</NavItem>
            <NavItem to="/contact">{tr.nav.contact}</NavItem>
          </nav>

          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={() => setSearchOpen((v) => !v)}
              className="p-2 rounded-full hover:bg-slate-100 transition-colors"
              aria-label="Search"
              data-testid="header-search-btn"
            >
              <Search className="w-5 h-5 text-slate-700" />
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="hidden sm:inline-flex items-center gap-1 px-3 py-2 rounded-full hover:bg-slate-100 text-sm font-medium text-slate-700" data-testid="header-lang-btn">
                  <Globe className="w-4 h-4" />{lang === "en" ? "EN" : "AR"}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLang("en")} data-testid="lang-en">English</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLang("ar")} data-testid="lang-ar">العربية</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="hidden md:inline-flex items-center px-3 py-2 rounded-full hover:bg-slate-100 text-sm font-medium text-slate-700" data-testid="header-currency-btn">
                  {currency}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setCurrency("AED")} data-testid="cur-aed">AED — UAE Dirham</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCurrency("USD")} data-testid="cur-usd">USD — US Dollar</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCurrency("IQD")} data-testid="cur-iqd">IQD — Iraqi Dinar</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-slate-100 transition-colors"
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              data-testid="theme-toggle"
            >
              {theme === "dark" ? <Sun className="w-5 h-5 text-slate-200" /> : <Moon className="w-5 h-5 text-slate-700" />}
            </button>

            <Link
              to="/request-quote"
              className="hidden xl:inline-flex items-center gap-1 border border-[#0B132B]/15 rounded-full px-4 py-2 text-sm font-medium text-[#0B132B] hover:border-[#1E5FD9] hover:text-[#1E5FD9] transition-colors"
              data-testid="header-quote-btn"
            >
              {tr.nav.quote} <ArrowRight className="w-3.5 h-3.5" />
            </Link>

            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex items-center gap-2 bg-[#1E5FD9] text-white hover:bg-[#174AB0] rounded-full px-4 py-2 text-sm font-medium transition-colors"
              data-testid="header-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4" />
              {tr.common.whatsappSales}
            </a>

            <Link
              to="/admin/login"
              className="p-2 rounded-full hover:bg-slate-100 transition-colors"
              aria-label="Admin"
              data-testid="header-admin-btn"
            >
              <ShieldCheck className="w-5 h-5 text-slate-700" />
            </Link>

            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden p-2 rounded-full hover:bg-slate-100"
              aria-label="Menu"
              data-testid="header-menu-btn"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {searchOpen && (
          <form onSubmit={submitSearch} className="pb-4">
            <div className="flex gap-2">
              <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr.common.search} className="rounded-full" data-testid="header-search-input" />
              <Button type="submit" className="rounded-full bg-[#1E5FD9] hover:bg-[#174AB0]" data-testid="header-search-submit">{tr.common.searchBtn}</Button>
            </div>
          </form>
        )}

        {open && (
          <div className="lg:hidden pb-4 space-y-1" data-testid="mobile-menu">
            {[
              ["/", tr.nav.home], ["/phones", tr.nav.phones],
              ["/phones?brand=Blackview", "Blackview"],
              ["/about", tr.nav.about], ["/contact", tr.nav.contact], ["/request-quote", tr.nav.quote],
            ].map(([to, label]) => (
              <Link
                key={to} to={to} onClick={() => setOpen(false)}
                className="block py-2.5 px-3 rounded-lg hover:bg-slate-100 text-slate-800 text-base"
                data-testid={`mobile-nav-${to === "/" ? "home" : to.slice(1).replace("/", "-")}`}
              >
                {label}
              </Link>
            ))}
            <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-200">
              <button onClick={() => setLang(lang === "en" ? "ar" : "en")} className="px-3 py-1.5 rounded-full bg-slate-100 text-sm" data-testid="mobile-lang-toggle">
                <Globe className="w-4 h-4 inline me-1" />{lang === "en" ? "العربية" : "English"}
              </button>
              {["AED", "USD", "IQD"].map((c) => (
                <button key={c} onClick={() => setCurrency(c)} className={`px-3 py-1.5 rounded-full text-sm ${currency === c ? "bg-[#1E5FD9] text-white" : "bg-slate-100"}`} data-testid={`mobile-cur-${c.toLowerCase()}`}>{c}</button>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
